export type PublicCategoryImage = {
  image_url: string;
  alt_text: string | null;
  sort_order: number;
};

export type PublicCategory = {
  pitch_category_id: string | null;
  name: string;
  available_count: number;
  nightly_rate?: string | null;
  rental_period?: string | null;
  total_amount?: string | null;
  nights?: number | null;
  price_pending?: boolean;
  teaser_text?: string | null;
  description_text?: string | null;
  features?: string[];
  hint_text?: string | null;
  badge_text?: string | null;
  highlight_recommended?: boolean;
  priority?: number;
  images?: PublicCategoryImage[];
  price_comparison_text?: string | null;
  upselling_enabled?: boolean;
};

export type PublicCategoryPrice = {
  pitch_category_id: string | null;
  nightly_rate?: string | null;
  rental_period?: string | null;
  total_amount?: string | null;
  nights?: number | null;
  price_pending?: boolean;
  price_comparison_text?: string | null;
};

export type CategoryPricesResponse = {
  arrival: string;
  departure: string;
  categories: PublicCategoryPrice[];
};

export type CategoriesResponse = {
  arrival: string;
  departure: string;
  categories: PublicCategory[];
  suggested_category_id?: string | null;
  period_blocked_message?: string | null;
};

export type UpsellingDisplaySettings = {
  upselling_enabled: boolean;
  auto_highlight_recommended: boolean;
  auto_select_best_available: boolean;
  show_price_comparison: boolean;
  show_category_images: boolean;
  show_marketing_texts: boolean;
  show_badges: boolean;
  sort_mode: "priority" | "price";
};

export type PriceQuoteLine = {
  rule_type: string;
  article_id: string;
  article_number: string;
  article_name: string;
  quantity: string;
  unit_price: string;
  line_subtotal: string;
  tax_rate_percent: string;
  tax_amount: string;
  line_total: string;
  billing_nights?: number | null;
};

export type PriceQuote = {
  nights: number;
  nightly_rate: string;
  subtotal: string;
  tax_rate_percent: string;
  tax_amount: string;
  total_amount: string;
  currency: string;
  price_pending?: boolean;
  lines: PriceQuoteLine[];
};

export type AlternativePeriod = {
  arrival: string;
  departure: string;
  nights: number;
  available_count: number;
  day_shift: number;
  departure_shift?: number;
};

export type AlternativePeriodsResponse = {
  requested_arrival: string;
  requested_departure: string;
  nights: number;
  alternatives: AlternativePeriod[];
};

export type CompanionFieldMode = "off" | "optional" | "required";

export type CompanionFieldsConfig = {
  title: CompanionFieldMode;
  first_name: CompanionFieldMode;
  middle_name: CompanionFieldMode;
  last_name: CompanionFieldMode;
  company_name: CompanionFieldMode;
  vat_id: CompanionFieldMode;
  email: CompanionFieldMode;
  phone: CompanionFieldMode;
  license_plate: CompanionFieldMode;
  age: CompanionFieldMode;
  date_of_birth: CompanionFieldMode;
  nationality: CompanionFieldMode;
  street: CompanionFieldMode;
  house_number: CompanionFieldMode;
  address_line_2: CompanionFieldMode;
  postal_code: CompanionFieldMode;
  city: CompanionFieldMode;
  state_region: CompanionFieldMode;
  country_code: CompanionFieldMode;
};

export type CompanionFieldsConfigByRole = {
  adult: CompanionFieldsConfig;
  child: CompanionFieldsConfig;
};

export type PublicArrivalTypeOption = {
  value: string;
  label: string;
  vehicle_fields?: Partial<
    Record<
      | "license_plate"
      | "vehicle_length_m"
      | "vehicle_width_m"
      | "vehicle_height_m"
      | "vehicle_weight_kg"
      | "tent_size",
      CompanionFieldMode
    >
  >;
  vehicle_field_tooltips?: Partial<Record<string, string>>;
  vehicle_field_max_values?: Partial<Record<string, string>>;
  vehicle_field_max_messages?: Partial<Record<string, string>>;
};

export type OnlineBookingFlowSettings = {
  show_electricity_option: boolean;
  default_wants_electricity: boolean;
  show_dogs_field: boolean;
  show_children_field: boolean;
  show_companion_details: boolean;
  require_guest_address: boolean;
  show_traveler_age_field: boolean;
};

export type PublicBookingAddonOption = {
  article_id: string;
  name: string;
  unit: string;
  billing: "once" | "per_night" | "per_adult" | "per_person";
  unit_price: string | null;
  description: string | null;
};

export type PublicPitchFeature = {
  id: string;
  name: string;
  description: string | null;
};

export type AddonSelection = {
  article_id: string;
  quantity: number;
};

export const DEFAULT_UPSELLING_DISPLAY: UpsellingDisplaySettings = {
  upselling_enabled: true,
  auto_highlight_recommended: true,
  auto_select_best_available: true,
  show_price_comparison: true,
  show_category_images: true,
  show_marketing_texts: true,
  show_badges: true,
  sort_mode: "priority",
};

export type OnlineBookingSeasonMode = "fixed" | "recurring";

export type OnlineBookingSeasonPeriod = {
  id: string;
  name: string;
  mode: OnlineBookingSeasonMode;
  start_date: string | null;
  end_date: string | null;
  start_month: number | null;
  start_day: number | null;
  end_month: number | null;
  end_day: number | null;
};

export type OnlineBookingMinStayPeriod = OnlineBookingSeasonPeriod & {
  min_nights: number;
};

export type OnlineBookingMinStaySettings = {
  enabled: boolean;
  default_min_nights: number;
  periods: OnlineBookingMinStayPeriod[];
};

export type OnlineBookingSameDayCutoffSettings = {
  enabled: boolean;
  cutoff_time: string;
};

export type PublicOnlineBookingLegal = {
  operator_name: string;
  legal_representative?: string | null;
  address_lines: string[];
  contact_email?: string | null;
  contact_phone?: string | null;
  privacy_email?: string | null;
  supervisory_authority?: string | null;
  imprint_url?: string | null;
  retention_note?: string | null;
  third_party_note?: string | null;
};

export type OnlineBookingMode = "inquiry" | "reservation";

export type PublicBookingConfig = {
  public_booking_enabled: boolean;
  online_booking_mode?: OnlineBookingMode;
  online_booking_availability_check?: boolean;
  site_name?: string;
  logo_url?: string | null;
  google_places_api_key?: string | null;
  companion_fields: CompanionFieldsConfig;
  companion_fields_adult?: CompanionFieldsConfig;
  companion_fields_child?: CompanionFieldsConfig;
  primary_guest_fields: CompanionFieldsConfig;
  arrival_types: PublicArrivalTypeOption[];
  flow: OnlineBookingFlowSettings;
  addon_articles: PublicBookingAddonOption[];
  upselling?: UpsellingDisplaySettings;
  booking_seasons?: OnlineBookingSeasonPeriod[];
  min_stay?: OnlineBookingMinStaySettings;
  same_day_cutoff?: OnlineBookingSameDayCutoffSettings;
  pitch_features?: PublicPitchFeature[];
  tourist_integration_enabled?: boolean;
  tourist_sold_out_nights?: string[];
  tourist_sold_out_message?: string | null;
  online_booking_legal?: PublicOnlineBookingLegal | null;
};

export type BookingResult = {
  booking: {
    id: string;
    booking_number: string;
    arrival_date: string;
    departure_date: string;
    total_amount: string;
    currency: string;
    status: string;
  };
  price: PriceQuote;
  created: boolean;
  message: string;
};

export type BookingHold = {
  hold_token: string;
  expires_at: string;
  expires_in_seconds: number;
  ttl_minutes: number;
};
